/**
 * PermissionGuard Component
 *
 * Industry-standard component for protecting UI elements based on permissions.
 * Use this component to conditionally render content based on user permissions.
 *
 * @example
 * // Single permission
 * <PermissionGuard permission="users.create">
 *   <CreateUserButton />
 * </PermissionGuard>
 *
 * @example
 * // Multiple permissions (any)
 * <PermissionGuard permissions={["users.edit", "users.delete"]} requireAll={false}>
 *   <ManageUserActions />
 * </PermissionGuard>
 *
 * @example
 * // With fallback
 * <PermissionGuard permission="reports.view" fallback={<AccessDenied />}>
 *   <ReportsPage />
 * </PermissionGuard>
 */

import { ReactNode } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { Shield, AlertTriangle } from "lucide-react";

// ============================================
// Types
// ============================================

interface PermissionGuardProps {
  /** Single permission to check */
  permission?: string;
  /** Multiple permissions to check */
  permissions?: string[];
  /** Whether all permissions are required (default: false = any) */
  requireAll?: boolean;
  /** Content to render if user has permission */
  children: ReactNode;
  /** Content to render if user doesn't have permission */
  fallback?: ReactNode;
  /** Whether to show loading state while checking permissions */
  showLoading?: boolean;
  /** Whether to show a styled access denied message (if no fallback provided) */
  showAccessDenied?: boolean;
}

// ============================================
// Default Access Denied Component
// ============================================

interface AccessDeniedProps {
  compact?: boolean;
}

export function AccessDenied({ compact = false }: AccessDeniedProps) {
  if (compact) {
    return (
      <div className="flex items-center gap-2 text-gray-500 text-sm">
        <Shield className="w-4 h-4" />
        <span>Access restricted</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-xl border border-gray-200">
      <div className="p-3 bg-amber-100 rounded-full mb-4">
        <AlertTriangle className="w-8 h-8 text-amber-700" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
      <p className="text-gray-500 text-center max-w-md">
        You don't have permission to access this content. Contact your administrator
        if you believe this is an error.
      </p>
    </div>
  );
}

// ============================================
// Loading Component
// ============================================

function PermissionLoading() {
  return (
    <div className="flex items-center justify-center p-8 min-h-[200px]">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin w-8 h-8 border-3 border-[#2C5F7C] border-t-transparent rounded-full" />
        <p className="text-sm text-gray-500">Checking permissions...</p>
      </div>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function PermissionGuard({
  permission,
  permissions,
  requireAll = false,
  children,
  fallback,
  showLoading = false,
  showAccessDenied = false,
}: PermissionGuardProps) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading } = usePermissions();

  // Show loading state if requested OR if showAccessDenied is true
  // This prevents flash of AccessDenied component while permissions load
  if (isLoading && (showLoading || showAccessDenied)) {
    return <PermissionLoading />;
  }

  // Don't render anything while loading (unless showLoading/showAccessDenied is true)
  if (isLoading) {
    return null;
  }

  // Check permissions
  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions && permissions.length > 0) {
    hasAccess = requireAll 
      ? hasAllPermissions(permissions) 
      : hasAnyPermission(permissions);
  } else {
    // No permission specified, allow access
    hasAccess = true;
  }

  // Render based on access
  if (hasAccess) {
    return <>{children}</>;
  }

  // No access - show fallback or access denied
  if (fallback) {
    return <>{fallback}</>;
  }

  if (showAccessDenied) {
    return <AccessDenied />;
  }

  // Default: render nothing
  return null;
}

// ============================================
// Admin-only Guard
// ============================================

interface AdminGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
  showAccessDenied?: boolean;
}

export function AdminGuard({ children, fallback, showAccessDenied }: AdminGuardProps) {
  const { isAdmin, isLoading } = usePermissions();

  // Show loading if showAccessDenied is true to prevent flash
  if (isLoading) {
    return showAccessDenied ? <PermissionLoading /> : null;
  }

  if (isAdmin()) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  if (showAccessDenied) {
    return <AccessDenied />;
  }

  return null;
}

// ============================================
// Super Admin-only Guard
// ============================================

interface SuperAdminGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
  showAccessDenied?: boolean;
}

export function SuperAdminGuard({ children, fallback, showAccessDenied }: SuperAdminGuardProps) {
  const { isSuperAdmin, isLoading } = usePermissions();

  // Show loading if showAccessDenied is true to prevent flash
  if (isLoading) {
    return showAccessDenied ? <PermissionLoading /> : null;
  }

  if (isSuperAdmin()) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  if (showAccessDenied) {
    return <AccessDenied />;
  }

  return null;
}

// ============================================
// Higher-Order Component (HOC) for Pages
// ============================================

export function withPermission<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  requiredPermission: string | string[],
  requireAll = false
) {
  return function PermissionProtectedComponent(props: P) {
    const permissionProps = Array.isArray(requiredPermission)
      ? { permissions: requiredPermission, requireAll }
      : { permission: requiredPermission };

    return (
      <PermissionGuard {...permissionProps} showAccessDenied>
        <WrappedComponent {...props} />
      </PermissionGuard>
    );
  };
}

export default PermissionGuard;
